import React, { Suspense, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Section, Box, Flex, TextField, Text, Button, Table, Dialog, Separator, RadioGroup } from '@radix-ui/themes';
import { useFormik } from 'formik';
import dayjs from 'dayjs';

import PageTitle from 'components/layout/PageTitle';
import { ConfirmDialog } from 'components/dialog';
import { FileUpload, DeleteButton } from 'components/form';
import Editor from 'components/editor';

import { instance } from 'api/axios.instance';
import ProjectYearField from './ProjectYearField';
import { isValidProjectYear, withProjectYearUpdate } from './projectYear';
import { validationSchemaProject } from 'util/validationSchemas';
import useRenderComponent from 'hooks/useRenderComponent';
import defaultThumbnail from 'assets/images/common/default_thumbnail.png';

// 사업유형 옵션
const BUSINESS_TYPE_OPTIONS = ['기술서비스개발및확산', '지원시설', '제도운영', '해외진출국제협력', '인력양성', '기타'];

// 지역 이슈 관리 > 신규 등록 목록 > 등록/수정 (지역 이슈 담당자 전용)
export default function ProjectRequestWrite() {
  // hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const { Components, handleRenderComponents, resetComponents } = useRenderComponent(); // 동적 컴포넌트 hook

  // 신규 등록의 기본 연도. 수정 시에는 서버에 저장된 분류 연도로 교체한다.
  const [registrationYear] = useState(() => String(new Date().getFullYear()));

  // state
  const [formValues, setFormValues] = useState({
    year: registrationYear,
    title: '', // 프로젝트 명
    description: '', // 설명
    source: '', // 사업유형
    reference: '', // 출처
    thumbnail: null, // 썸네일
    agendaData: [], // 아젠다
    deletedFileIds: [] // 삭제된 파일 ID 목록
  });
  const [businessType, setBusinessType] = useState(''); // 사업유형 라디오 선택값
  const [customBusinessType, setCustomBusinessType] = useState(''); // 기타 사업유형 텍스트
  const [isEditMode, setIsEditMode] = useState(false); // 수정 모드
  const [validationSchema, setValidationSchema] = useState(null); // 유효성 검사 스키마
  const [alias, setAlias] = useState('2'); // 별칭 아젠다 (지역현안, 아이디어, 프로젝트)
  const [isOpenSaveDialog, setIsOpenSaveDialog] = useState(false); // 프로젝트 저장 모달
  const [agendaList, setAgendaList] = useState([]);
  const [selectedAgenda, setSelectedAgenda] = useState('');
  const [templateOrders, setTemplateOrders] = useState([]); // 템플릿 순서 정보 저장

  // 현재 입력된 값들을 저장하는 ref
  const currentInputValuesRef = useRef({
    year: registrationYear,
    title: '',
    description: '',
    source: '',
    reference: '', // 출처
    thumbnail: null,
    // alias: ''
    agendaCode: ''
  });

  // form 유효성 검사
  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validationSchema || undefined, // null이면 undefined로 설정
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: values => {
      // 필수 항목 검증
      const errors = [];
      // 사업명 검증
      if (!values.title || !values.title.trim()) {
        errors.push('사업명');
      }
      // 사업소개 검증
      if (!values.description || !values.description.trim()) {
        errors.push('사업소개');
      }
      // 사업유형 검증
      if (!values.source || !values.source.trim()) {
        errors.push('사업유형');
      }
      // 출처 검증
      if (!values.reference || !values.reference.trim()) {
        errors.push('출처');
      }
      // 썸네일 검증 (선택사항으로 변경)
      // if (!values.thumbnail) {
      //   errors.push('썸네일');
      // }
      // 신규 등록 모드에서만 아젠다 선택 여부 검증
      if (!isEditMode) {
        if (!selectedAgenda) {
          errors.push('아젠다 선택');
        }
        // 아젠다 데이터가 비어있는지 검증
        if (!values.agendaData || values.agendaData.length === 0) {
          if (!errors.includes('아젠다 선택')) {
            errors.push('아젠다 선택');
          }
        }
      }
      if (!isValidProjectYear(values.year)) {
        errors.push('분류 연도(1000~9999)');
      }
      // 에러가 있으면 알림 표시
      if (errors.length > 0) {
        alert(`다음 필수 항목을 입력해주세요:\n\n• ${errors.join('\n• ')}`);
        return;
      }
      setIsOpenSaveDialog(true);
      setFormValues(values);
    }
  });

  // 썸네일 파일 첨부 Method
  const handleFileChange = file => {
    const size = file.size > 50 * 1024 * 1024;

    if (size) {
      return formik.setFieldError('thumbnail', '파일은 50MB를 넘을 수 없습니다.');
    }
    currentInputValuesRef.current.thumbnail = file;
    formik.setFieldValue('thumbnail', file);
  };

  // 사업유형 선택 Method
  const handleBusinessTypeChange = value => {
    setBusinessType(value);
    if (value === '기타') {
      currentInputValuesRef.current.source = customBusinessType;
      formik.setFieldValue('source', customBusinessType);
    } else {
      currentInputValuesRef.current.source = value;
      formik.setFieldValue('source', value);
      setCustomBusinessType('');
    }
  };

  // 기타 사업유형 텍스트 입력 Method
  const handleCustomBusinessTypeChange = value => {
    setCustomBusinessType(value);
    formik.setFieldValue('source', value);
  };

  // ChatGPT 프롬프트 생성 Method
  const generateImagePrompt = () => {
    const title = formik.values.title?.trim();
    const description = formik.values.description?.trim();
    if (!title || !description) {
      alert('사업명과 사업소개를 먼저 입력해주세요.');
      return '';
    }
    return `Create a professional 16:9 aspect ratio thumbnail image (400px width) for a business project.

Project Title: "${title}"
Project Description: "${description}"

Design Requirements:
- 16:9 aspect ratio (400px width)
- Professional and modern design
- Relevant visual elements that represent the project
- delete the text from the thumbnail image

Style : Do it in a bright, high-tech style`;
  };

  // 클립보드 복사 Method
  const copyToClipboard = async text => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alert('프롬프트가 클립보드에 복사되었습니다!');
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
      // 대체 방법
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('프롬프트가 클립보드에 복사되었습니다!');
    }
  };

  // 프로젝트 생성 API
  const handleCreateProject = async () => {
    if (!isValidProjectYear(formValues.year)) {
      alert('분류 연도는 1000~9999 사이의 네 자리 숫자로 입력해주세요.');
      return;
    }
    const type = isEditMode ? 'edit' : 'create';
    const formData = new FormData();

    // 페이지 코드 생성 (PJ- + 현재 시간 기반 랜덤 문자열)
    const generatePageCode = () => {
      const timestamp = Date.now().toString();
      const randomString = Math.random().toString(36).substring(2, 8);
      return `PJ-${timestamp.slice(-8)}${randomString}`;
    };

    // agendaData를 저장 직전에 한 번 더 필터링 (빈 템플릿, 메타 없는 항목 제거)
    const agendaDataToUse = formik.values.agendaData || formValues.agendaData || [];

    // 갤러리 이미지들을 별도로 처리
    const gallery = [];

    const filteredAgendaData = agendaDataToUse
      .filter(data => {
        return (
          data &&
          data.templateName &&
          data.componentName &&
          data.templateData &&
          Object.values(data.templateData).some(v => v && v.toString().trim())
        );
      })
      .map(data => {
        const orderFromTemplate = templateOrders.find(order => order.templateCode === data.templateCode)?.order || 0;

        const agendaCodeToUse =
          data.agendaCode ||
          selectedAgenda ||
          (isEditMode && formValues.agendaData?.[0]?.agendaCode) ||
          agendaDataToUse.find(item => item.agendaCode)?.agendaCode ||
          '';

        // ImageGallery 템플릿의 gallery 처리
        if (data.templateCode === 'TP021' && data.templateData.gallery) {
          const remainingFiles = [];

          // File 객체들을 FormData gallery에 추가하고, 기존 파일 객체들은 templateData에 보관
          data.templateData.gallery.forEach(image => {
            if (image instanceof File) {
              gallery.push(image);
            } else if (image && typeof image === 'object' && image.fileId) {
              // 기존 서버 파일 객체 (fileId, fileUrl, originalName 등 포함)
              remainingFiles.push(image);
            }
          });

          // templateData에는 기존 파일 객체들만 포함 (삭제되지 않은 것들)
          return {
            ...data,
            order: orderFromTemplate,
            agendaCode: agendaCodeToUse,
            isUsed: data.isUsed !== undefined ? data.isUsed : true,
            templateData: {
              ...data.templateData,
              gallery: remainingFiles
            }
          };
        }

        return {
          ...data,
          order: orderFromTemplate,
          agendaCode: agendaCodeToUse,
          isUsed: data.isUsed !== undefined ? data.isUsed : true
        };
      });

    const attachFiles = [];

    filteredAgendaData.forEach(data => {
      if (data.templateData && Array.isArray(data.templateData.attachFiles)) {
        data.templateData.attachFiles.forEach(file => {
          if (file && file.name) {
            attachFiles.push(file);
          }
        });
      }
    });

    // 썸네일 처리: 사용자가 업로드한 파일이 있으면 사용, 없으면 기본 이미지 사용
    if (formValues.thumbnail && formValues.thumbnail.name) {
      formData.append('thumbnail', formValues.thumbnail);
    } else if (!formValues.thumbnail) {
      // 기본 썸네일 이미지를 File 객체로 변환하여 FormData에 추가
      const response = await fetch(defaultThumbnail);
      const blob = await response.blob();
      const defaultFile = new File([blob], 'default_thumbnail.png', { type: 'image/png' });
      formData.append('thumbnail', defaultFile);
    }

    const projectData = withProjectYearUpdate({
      stage: `${alias}단계`,
      ...formValues,
      thumbnail: formValues.thumbnail && Object.keys(formValues.thumbnail).length > 0 ? formValues.thumbnail : defaultThumbnail,
      agendaCode: filteredAgendaData?.[0]?.agendaCode || selectedAgenda || '',
      agendaData: filteredAgendaData,
      deletedFileIds: formik.values.deletedFileIds || [] // formik에서 삭제된 파일 ID 가져오기
    });

    // 수정 모드일 때만 ID 포함
    if (isEditMode && id) {
      projectData.id = id;
    }

    formData.append('body', JSON.stringify(projectData));

    // attachFiles를 FormData에 추가
    attachFiles.forEach(file => {
      formData.append('attachFiles', file);
    });

    // 갤러리 이미지들을 FormData에 추가
    gallery.forEach(image => {
      formData.append('gallery', image);
    });

    console.log('전송될 첨부파일 수:', attachFiles.length);
    console.log('전송될 갤러리 이미지 수:', gallery.length);
    console.log('전송될 projectData:', projectData);

    const response = await instance(`/project/${type}`, {
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.data.status === 201) {
      navigate('/projectRequest');
    }
  };

  // 프로젝트 상세 조회 API
  const fetchAgendaById = useCallback(async () => {
    const response = await instance(`/project/${id}`, {
      method: 'GET'
    });

    if (response.status === 200) {
      // 아젠다 코드를 여러 위치에서 찾아보기
      const agendaCodeFromProject = response.data.data.agendaCode;
      const agendaCodeFromData = response.data.data.data[0]?.agendaCode;
      const agendaCode = agendaCodeFromProject || agendaCodeFromData;

      // 서버에서 받은 기존 프로젝트 데이터
      const existingProjectData = response.data.data.data;

      if (agendaCode) {
        setSelectedAgenda(agendaCode);

        try {
          // 아젠다 정보와 템플릿 정보 조회
          const agendaResponse = await instance(`/admin/agenda/${agendaCode}`, { method: 'GET' });
          const templateOrders = agendaResponse.data.data.templateOrders || [];
          const allTemplates = agendaResponse.data.data.templates || [];

          setTemplateOrders(templateOrders);

          // templateOrders에 있는 모든 템플릿을 순서대로 정렬하여 컴포넌트 렌더링
          const sortedTemplates = allTemplates
            .filter(template => templateOrders.some(order => order.templateCode === template.templateCode))
            .sort((a, b) => {
              const orderA = templateOrders.find(order => order.templateCode === a.templateCode)?.order || 999;
              const orderB = templateOrders.find(order => order.templateCode === b.templateCode)?.order || 999;
              return orderA - orderB;
            });

          // 모든 템플릿의 컴포넌트를 렌더링
          resetComponents();
          handleRenderComponents(sortedTemplates.map(template => template.componentName));

          // templateOrders에 있는 모든 템플릿을 기준으로 agendaData 생성
          const allAgendaData = templateOrders
            .map(orderInfo => {
              const template = allTemplates.find(t => t.templateCode === orderInfo.templateCode);
              const existingData = existingProjectData.find(data => data.templateCode === orderInfo.templateCode);

              // 디버깅: gallery 데이터 확인
              if (orderInfo.templateCode === 'TP021' && existingData?.templateData?.gallery) {
                console.log('서버에서 받아온 gallery 데이터:', existingData.templateData.gallery);
                console.log('gallery 첫 번째 아이템:', existingData.templateData.gallery[0]);
              }

              return {
                templateCode: orderInfo.templateCode,
                templateData: existingData?.templateData || {},
                agendaCode: existingData?.agendaCode || agendaCode,
                templateName: template?.templateName || existingData?.templateName || '',
                componentName: template?.componentName || existingData?.componentName || '',
                componentPath: template?.componentPath || existingData?.componentPath || '',
                stageDesc: template?.stageDesc || existingData?.stageDesc || '',
                isUsed: template?.isUsed !== undefined ? template.isUsed : existingData?.isUsed !== undefined ? existingData.isUsed : true,
                order: orderInfo.order
              };
            })
            .sort((a, b) => a.order - b.order);

          setAlias(response.data.data.stage.split('단계')[0]);
          const sourceValue = response.data.data.source || '';

          // 기존 source 값이 정의된 사업유형 중 하나인지 확인
          const isPreDefinedType = BUSINESS_TYPE_OPTIONS.includes(sourceValue);
          if (isPreDefinedType) {
            setBusinessType(sourceValue);
            setCustomBusinessType('');
          } else if (sourceValue) {
            setBusinessType('기타');
            setCustomBusinessType(sourceValue);
          }

          const newFormValues = {
            year: String(response.data.data.year ?? ''),
            title: response.data.data.title || '',
            description: response.data.data.description || '',
            source: sourceValue,
            reference: response.data.data.reference,
            stage: `${response.data.data.stage.split('단계')[0]}단계`,
            thumbnail: response.data.data.thumbnail,
            agendaData: allAgendaData
          };

          setFormValues(newFormValues);

          setTimeout(() => {
            if (formik?.setValues) {
              formik.setValues(newFormValues);
            }
          }, 100);
        } catch (error) {
          console.error('아젠다 정보 조회 실패:', error);
        }
      }
    }
  }, [handleRenderComponents, id, resetComponents]);

  // mount 시 param id 여부에 따라 호출
  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchAgendaById();
    } else {
      setIsEditMode(false);
    }
  }, [fetchAgendaById, id]);

  // mount 시 유효성 검사 동적 스키마 적용 (참여기관 및 주최, 제안자 인적 사항 템플릿 포함 여부에 따라 스키마를 동적으로 적용)
  useEffect(() => {
    setValidationSchema(
      validationSchemaProject({
        personalInfoIndex: formValues.agendaData.findIndex(data => data.templateCode === 'TP011'),
        organizationIndex: formValues.agendaData.findIndex(data => data.templateCode === 'TP014')
      })
    );
  }, [formValues]);

  // 아젠다 리스트 불러오기
  useEffect(() => {
    async function fetchAgendaList() {
      const response = await instance('/admin/agenda/list', { method: 'POST', data: { pageNo: 1 } });
      setAgendaList(response.data.data.agendas);
    }
    fetchAgendaList();
  }, []);

  // 아젠다 상세 불러오기 함수
  const fetchAgendaDetail = async agendaCode => {
    if (!agendaCode) return;
    const response = await instance(`/admin/agenda/${agendaCode}`, { method: 'GET' });

    resetComponents();

    // isUsed가 true인 템플릿만 추출
    const usedTemplates = response.data.data.templates.filter(t => t.isUsed === true);

    // templateOrders 배열을 참조하여 order 순서대로 정렬
    const templateOrders = response.data.data.templateOrders || [];
    setTemplateOrders(templateOrders); // templateOrders 상태에 저장

    const sortedTemplates = usedTemplates.sort((a, b) => {
      const orderA = templateOrders.find(order => order.templateCode === a.templateCode)?.order || 999;
      const orderB = templateOrders.find(order => order.templateCode === b.templateCode)?.order || 999;
      return orderA - orderB;
    });

    const componentNames = sortedTemplates.map(t => t.componentName);
    handleRenderComponents(componentNames);

    const currentInputs = currentInputValuesRef.current;

    let agendaData;
    if (isEditMode) {
      const existingAgendaData = formValues.agendaData || [];
      agendaData = sortedTemplates.map(template => {
        const existingData = existingAgendaData.find(data => data.templateCode === template.templateCode);
        return {
          ...template,
          agendaCode: existingData?.agendaCode || agendaCode,
          order: templateOrders.find(order => order.templateCode === template.templateCode)?.order || 0,
          templateData: existingData?.templateData || template.templateData || {}
        };
      });
    } else {
      agendaData = sortedTemplates.map(template => ({
        ...template,
        agendaCode: agendaCode,
        order: templateOrders.find(order => order.templateCode === template.templateCode)?.order || 0,
        templateData: {}
      }));
    }

    const updatedValues = {
      year: currentInputs.year || '',
      title: currentInputs.title || '',
      description: currentInputs.description || '',
      source: currentInputs.source || '',
      reference: currentInputs.reference || '',
      thumbnail: currentInputs.thumbnail || null,
      agendaData: agendaData
    };

    setFormValues(updatedValues);

    if (!isEditMode) {
      setTimeout(() => {
        if (formik?.setFieldValue) {
          formik.setFieldValue('agendaData', updatedValues.agendaData);
        }
      }, 100);
    }
  };

  // 오늘 날짜를 컴포넌트 마운트 시 한 번만 계산
  const todayString = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  // agendaList를 조건에 따라 필터링 (isUsed가 true인 것만)
  const filteredAgendaList = useMemo(() => {
    return agendaList.filter(agenda => {
      // isUsed가 false인 아젠다는 화면에 렌더링하지 않음
      const startDate = dayjs(agenda.startDate).format('YYYY-MM-DD');
      const endDate = dayjs(agenda.endDate).format('YYYY-MM-DD');

      return agenda.isUsed === true && startDate <= todayString && endDate >= todayString;
    });
  }, [agendaList, todayString]);

  // selectedAgenda가 변경될 때 아젠다 상세 정보 가져오기 (신규 등록 모드에서만)
  useEffect(() => {
    if (!isEditMode) {
      if (selectedAgenda) {
        fetchAgendaDetail(selectedAgenda);
      } else {
        resetComponents();
      }
    }
  }, [selectedAgenda, resetComponents, isEditMode]);

  return (
    <Section className="template-form-wrap">
      <PageTitle title={`프로젝트 ${isEditMode ? '수정' : '등록'}`} />
      <form
        onSubmit={e => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <Box className="box box-white">
          <Box mb="var(--space-10)">
            <Table.Root className="table-write">
              <Table.Body>
                <Table.Row align="center">
                  <Table.ColumnHeaderCell colSpan={2}>
                    <Text className="required-start">분류 연도</Text>
                  </Table.ColumnHeaderCell>
                  <Table.Cell colSpan={10}>
                    <ProjectYearField
                      value={formik.values.year}
                      onChange={e => {
                        currentInputValuesRef.current.year = e.target.value;
                        formik.handleChange(e);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.year && formik.errors.year}
                    />
                  </Table.Cell>
                </Table.Row>
                <Table.Row align="center">
                  <Table.ColumnHeaderCell colSpan={2}>
                    <Text className="required-start">사업명</Text>
                  </Table.ColumnHeaderCell>
                  <Table.Cell colSpan={10}>
                    <TextField.Root
                      size="2"
                      id="title"
                      name="title"
                      required
                      value={formik.values.title}
                      onChange={e => {
                        currentInputValuesRef.current.title = e.target.value;
                        formik.handleChange(e);
                      }}
                      onBlur={formik.handleBlur}
                      placeholder="사업명을 입력하세요"
                    />
                    {formik.touched.title && formik.errors.title && (
                      <Text as="p" mt="var(--space-1)" className="font-error body3">
                        {formik.errors.title}
                      </Text>
                    )}
                  </Table.Cell>
                </Table.Row>
                <Table.Row align="center">
                  <Table.ColumnHeaderCell colSpan={2}>
                    <Text className="required-start">사업소개</Text>
                  </Table.ColumnHeaderCell>
                  <Table.Cell colSpan={10}>
                    <Editor
                      editorData={formik.values.description}
                      setEditorData={data => {
                        currentInputValuesRef.current.description = data;
                        formik.setFieldValue('description', data);
                      }}
                      onBlur={() => formik.setFieldTouched('description', true)}
                    />
                    {formik.touched.description && formik.errors.description && (
                      <Text as="p" mt="var(--space-1)" className="font-error body3">
                        {formik.errors.description}
                      </Text>
                    )}
                  </Table.Cell>
                </Table.Row>
                <Table.Row align="center">
                  <Table.ColumnHeaderCell colSpan={2}>
                    <Text className="required-start">사업유형</Text>
                  </Table.ColumnHeaderCell>
                  <Table.Cell colSpan={10}>
                    <RadioGroup.Root size="2" value={businessType} onValueChange={handleBusinessTypeChange}>
                      <Flex
                        direction="row"
                        gap="6"
                        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(3, 1fr)', gap: '12px' }}
                      >
                        {BUSINESS_TYPE_OPTIONS.slice(0, 5).map(option => (
                          <Text key={option} as="label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <RadioGroup.Item value={option} />
                            {option}
                          </Text>
                        ))}
                        <Flex align="center" gap="2">
                          <Text as="label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <RadioGroup.Item value="기타" />
                            기타
                          </Text>
                          <TextField.Root
                            size="2"
                            placeholder="기타 사업유형을 입력하세요"
                            value={customBusinessType}
                            onChange={e => handleCustomBusinessTypeChange(e.target.value)}
                            disabled={businessType !== '기타'}
                            style={{
                              marginLeft: '8px',
                              minWidth: '200px',
                              opacity: businessType !== '기타' ? 0.5 : 1
                            }}
                          />
                        </Flex>
                      </Flex>
                    </RadioGroup.Root>
                    {formik.touched.source && formik.errors.source && (
                      <Text as="p" mt="var(--space-1)" className="font-error body3">
                        {formik.errors.source}
                      </Text>
                    )}
                  </Table.Cell>
                </Table.Row>
                <Table.Row align="center">
                  <Table.ColumnHeaderCell colSpan={2}>
                    <Text className="required-start">출처명</Text>
                  </Table.ColumnHeaderCell>
                  <Table.Cell colSpan={10}>
                    <TextField.Root
                      size="2"
                      id="reference"
                      name="reference"
                      required
                      value={formik.values.reference}
                      onChange={e => {
                        currentInputValuesRef.current.reference = e.target.value;
                        formik.handleChange(e);
                      }}
                      onBlur={formik.handleBlur}
                      placeholder="출처를 입력하세요"
                    />
                    {formik.touched.reference && formik.errors.reference && (
                      <Text as="p" mt="var(--space-1)" className="font-error body3">
                        {formik.errors.reference}
                      </Text>
                    )}
                  </Table.Cell>
                </Table.Row>
                <Table.Row align="center">
                  <Table.ColumnHeaderCell colSpan={2}>
                    <Text>썸네일 첨부</Text>
                  </Table.ColumnHeaderCell>
                  <Table.Cell colSpan={10}>
                    <Flex direction="column" align="start" px="var(--space-1)">
                      <FileUpload
                        label="파일선택"
                        id="thumbnail-upload"
                        accept="image/*"
                        message="IMG 파일, 개당 50MB"
                        multiple={false}
                        onFileChange={handleFileChange}
                      />
                      <Box mt="3" p="3" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                        <Text as="p" size="2" weight="medium" mb="2">
                          🎨 AI로 썸네일 이미지 생성하기
                        </Text>
                        <Text as="p" size="1" color="gray" mb="3">
                          사업명과 사업소개를 바탕으로 ChatGPT에서 썸네일 이미지를 생성할 수 있습니다.
                        </Text>
                        <Flex gap="2" align="center" wrap="wrap">
                          <Button
                            size="1"
                            variant="outline"
                            type="button"
                            onClick={() => copyToClipboard(generateImagePrompt())}
                            style={{ fontSize: '12px' }}
                          >
                            📋 프롬프트 복사
                          </Button>
                          <Button
                            size="1"
                            variant="soft"
                            type="button"
                            onClick={() => window.open('https://chatgpt.com', '_blank')}
                            style={{ fontSize: '12px' }}
                          >
                            🔗 ChatGPT 열기
                          </Button>
                        </Flex>
                        <Text as="p" size="1" color="gray" mt="2">
                          💡 팁: 사업명과 사업소개를 먼저 작성한 후 프롬프트를 복사하세요.
                        </Text>
                      </Box>
                    </Flex>
                    <Box mt="2">
                      <img
                        src={
                          formik.values.thumbnail
                            ? formik.values.thumbnail.fileUrl || URL.createObjectURL(formik.values.thumbnail)
                            : defaultThumbnail
                        }
                        alt="썸네일 미리보기"
                        style={{ width: '400px' }}
                      />
                      {formik.values.thumbnail && (
                        <Box className="file-item" mt="2">
                          <Text as="p" color="gray">
                            {formik.values.thumbnail.originalName || formik.values.thumbnail.fileName}
                          </Text>
                          <DeleteButton onDelete={() => formik.setFieldValue('thumbnail', null)} />
                        </Box>
                      )}
                      {!formik.values.thumbnail && (
                        <Box mt="2">
                          <Text as="p" color="gray" size="1">
                            기본 썸네일 이미지가 사용됩니다.
                          </Text>
                        </Box>
                      )}
                    </Box>
                    {formik.touched.thumbnail && formik.errors.thumbnail && (
                      <Text as="p" mt="var(--space-1)" className="font-error body3">
                        {formik.errors.thumbnail}
                      </Text>
                    )}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </Box>

          {/* 신규 등록 모드에서만 아젠다 선택 UI 표시 */}
          {!isEditMode && (
            <Box mb="var(--space-10)">
              <Box mb="5">
                <Text as="h3" size="3" weight="bold">
                  작성하실 아젠다를 선택하여 주세요
                </Text>
              </Box>
              {filteredAgendaList.length === 0 ? (
                <Box p="4" style={{ backgroundColor: 'var(--red-2)', border: '1px solid var(--red-6)', borderRadius: 'var(--radius-2)' }}>
                  <Text size="2" color="red" weight="medium">
                    작성하실 아젠다가 없습니다.
                  </Text>
                </Box>
              ) : (
                <Flex direction="column" gap="var(--space-3)">
                  <RadioGroup.Root size="3" color="blue" value={selectedAgenda} onValueChange={setSelectedAgenda}>
                    <Flex direction="column" gap="var(--space-3)">
                      {filteredAgendaList.map(agenda => (
                        <Flex key={agenda.agendaCode} align="center" gap="var(--space-2)">
                          <RadioGroup.Item value={agenda.agendaCode} />
                          <Text size="2" weight="medium">
                            {agenda.agendaName}
                          </Text>
                          <Text size="1" color="gray">
                            ({dayjs(agenda.startDate).format('YYYY-MM-DD')} ~ {dayjs(agenda.endDate).format('YYYY-MM-DD')})
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  </RadioGroup.Root>
                </Flex>
              )}
            </Box>
          )}

          {/* 수정 모드에서 현재 아젠다 정보 표시 */}
          {isEditMode && selectedAgenda && (
            <Box mb="var(--space-5)">
              <Box mb="3">
                <Text as="h3" size="3" weight="bold">
                  현재 아젠다
                </Text>
              </Box>
              <Box p="3" style={{ backgroundColor: 'var(--blue-2)', border: '1px solid var(--blue-6)', borderRadius: 'var(--radius-2)' }}>
                {(() => {
                  const currentAgenda = agendaList.find(agenda => agenda.agendaCode === selectedAgenda);
                  return currentAgenda ? (
                    <Flex align="center" gap="var(--space-2)">
                      <Text size="2" weight="medium" color="blue">
                        📋 {currentAgenda.agendaName}
                      </Text>
                      <Text size="1" color="gray">
                        ({dayjs(currentAgenda.startDate).format('YYYY-MM-DD')} ~ {dayjs(currentAgenda.endDate).format('YYYY-MM-DD')})
                      </Text>
                    </Flex>
                  ) : (
                    <Text size="2" color="blue">
                      아젠다 정보를 불러오는 중...
                    </Text>
                  );
                })()}
              </Box>
            </Box>
          )}

          <Separator size="4" mb="var(--space-5)" />

          {/* 아젠다 */}
          {Components.length > 0 && (
            <Suspense fallback={<div />}>
              {Components.map((Component, index) => (
                <Component key={index} index={index} formik={formik} setFormValues={setFormValues} isWriteMode={false} />
              ))}
            </Suspense>
          )}
        </Box>

        <Box>
          {/* 액션 버튼들 - 하단 가운데 */}
          <Flex gap="var(--space-2)" justify="center">
            <Button size="2" type="submit">
              저장
            </Button>
            <Dialog.Root open={isOpenSaveDialog} onOpenChange={setIsOpenSaveDialog}>
              <ConfirmDialog title="저장 확인" description="저장하시겠습니까?" onConfirm={handleCreateProject} />
            </Dialog.Root>

            <Dialog.Root>
              <Dialog.Trigger>
                <Button size="2" variant="outline" type="button">
                  취소
                </Button>
              </Dialog.Trigger>
              <ConfirmDialog
                title="저장 취소"
                description="작성중인 내용을 저장하지 않고 나가시겠습니까?"
                onConfirm={() => navigate('/projectRequest')}
              />
            </Dialog.Root>
          </Flex>
        </Box>
      </form>
    </Section>
  );
}
